import { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@/components/dialog";
import Button from "../../button";
import {
	StyledError,
	StyledInputContainer,
	StyledLabel,
} from "../input-styles";
import {
	IconContainer,
	StyledInput,
	StyledInputIconContainer,
} from "../input-text/styles";
import {
	FileListWrapper,
	HintText,
	ImageContainer,
	ImagePreview,
} from "./styles";

interface Image {
	name: string;
	signed_id: string;
	thumbnail: string;
}

interface Props {
	id?: string;
	label?: string;
	error?: string;
	disabled?: boolean;
	icon?: React.ReactNode;
	hint?: string;
	onFilesChange?: (files: (File | Image)[] | null) => void;
	value?: (File | Image)[];
}

const InputFile = forwardRef<HTMLInputElement, Props>(
	(
		{
			id,
			label,
			error,
			icon,
			disabled,
			onFilesChange,
			hint,
			value = [],
			...props
		},
		ref,
	) => {
		const { t } = useTranslation();
		const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

		const handleFileChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const newFiles = e.target.files;
				if (newFiles) {
					const updatedFiles = [...value, ...Array.from(newFiles)];
					onFilesChange?.(updatedFiles);
					e.target.value = "";
				}
			},
			[value, onFilesChange],
		);

		const handleRemoveFileConfirm = useCallback(() => {
			if (selectedIndex !== null) {
				const updatedFiles = (value ?? []).filter(
					(_, i) => i !== selectedIndex,
				);
				onFilesChange?.(updatedFiles);
				setSelectedIndex(null);
			}
		}, [selectedIndex, value, onFilesChange]);

		return (
			<StyledInputContainer>
				{label && <StyledLabel htmlFor={id}>{label}</StyledLabel>}

				<StyledInputIconContainer>
					<StyledInput
						ref={ref}
						disabled={disabled}
						type="file"
						accept="image/*"
						multiple
						id={id}
						onChange={handleFileChange}
						{...props}
					/>
					{icon && <IconContainer>{icon}</IconContainer>}
				</StyledInputIconContainer>

				<FileListWrapper>
					{value?.map((item, index) => (
						<ImageContainer
							key={
								item instanceof File
									? `${item.name}-${item.size}-${item.lastModified}`
									: item.signed_id
							}
						>
							<ImagePreview
								src={
									item instanceof File
										? URL.createObjectURL(item)
										: `${import.meta.env.VITE_API_URL}${item.thumbnail}`
								}
								alt={item.name}
							/>
							<Button
								color="red"
								size="sm"
								type="button"
								onClick={() => setSelectedIndex(index)}
							>
								Supprimer
							</Button>
						</ImageContainer>
					))}
				</FileListWrapper>

				{error && <StyledError>{error}</StyledError>}
				{hint && <HintText>{hint}</HintText>}

				<Dialog
					open={selectedIndex !== null}
					onClose={() => setSelectedIndex(null)}
					title={t("product.deleteImageTitle")}
					footer={
						<>
							<Button variant="outline" onClick={() => setSelectedIndex(null)}>
								{t("global.cancel")}
							</Button>
							<Button onClick={handleRemoveFileConfirm}>
								{t("global.confirm")}
							</Button>
						</>
					}
				>
					{t("product.deleteImageDescription")}
				</Dialog>
			</StyledInputContainer>
		);
	},
);

InputFile.displayName = "InputFile";

export default InputFile;
